import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { Scan, CheckCircle2, Heart, Smartphone, X, Coffee, Download, Share, Crown, Star } from 'lucide-react';
import { toast } from 'sonner';

export const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showWelcome, setShowWelcome] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    
    // Detect if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);
    
    // Listen for PWA install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isStandalone) {
      toast.success(t('alreadyInstalled') || '¡La app ya está instalada!');
      return;
    }
    
    if (deferredPrompt) {
      // Chrome/Android - use native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success(t('appInstalled') || '¡App instalada correctamente!');
      }
      setDeferredPrompt(null);
    } else {
      // iOS or browser that doesn't support install prompt
      setShowInstallModal(true);
    }
  };

  const languages = [
    { code: 'es', name: 'Español' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' },
    { code: 'ca', name: 'Català' },
    { code: 'eu', name: 'Euskara' },
    { code: 'gl', name: 'Galego' },
    { code: 'it', name: 'Italiano' }
  ];

  const features = [
    {
      icon: <Scan className="w-6 h-6 text-[#8FBC8F]" />,
      text: t('scanBarcodes')
    },
    {
      icon: <CheckCircle2 className="w-6 h-6 text-[#8FBC8F]" />,
      text: t('identifyAllergens')
    },
    {
      icon: <Heart className="w-6 h-6 text-[#8FBC8F]" />,
      text: t('customList')
    },
    {
      icon: <Smartphone className="w-6 h-6 text-[#8FBC8F]" />,
      text: t('worksPWA')
    }
  ];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setCurrentLang(langCode);
  };

  const handleClose = () => {
    setShowWelcome(true);
  };

  const handleCloseWelcome = () => {
    localStorage.setItem('hasSeenWelcome', 'true');
    setShowWelcome(false);
    window.location.href = '/scan';
  };

  return (
    <>
      {/* Install Instructions Modal (for iOS) */}
      <Dialog open={showInstallModal} onOpenChange={setShowInstallModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <img src="/logo-transparent.png" alt="AllergenApp Logo" className="w-20 h-20" />
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-center">
              {t('installInstructions') || 'Instalar en tu dispositivo'}
            </h3>
            
            {isIOS ? (
              <div className="space-y-3 text-sm text-gray-700">
                <p className="font-medium">{t('iosInstallTitle') || 'Para instalar en iPhone/iPad:'}</p>
                <ol className="list-decimal list-inside space-y-2">
                  <li>{t('iosStep1') || 'Toca el botón de compartir'} <Share className="w-4 h-4 inline text-blue-500" /></li>
                  <li>{t('iosStep2') || 'Desliza hacia abajo y toca "Añadir a pantalla de inicio"'}</li>
                  <li>{t('iosStep3') || 'Toca "Añadir" en la esquina superior derecha'}</li>
                </ol>
              </div>
            ) : (
              <div className="space-y-3 text-sm text-gray-700">
                <p className="font-medium">{t('androidInstallTitle') || 'Para instalar:'}</p>
                <ol className="list-decimal list-inside space-y-2">
                  <li>{t('androidStep1') || 'Abre el menú de tu navegador (⋮)'}</li>
                  <li>{t('androidStep2') || 'Selecciona "Instalar aplicación" o "Añadir a pantalla de inicio"'}</li>
                  <li>{t('androidStep3') || 'Confirma la instalación'}</li>
                </ol>
              </div>
            )}
            
            <div className="pt-4 space-y-2">
              <Button 
                onClick={() => {
                  setShowInstallModal(false);
                  navigate('/scan');
                }}
                className="w-full bg-[#8FBC8F] hover:bg-[#7CAA7C]"
              >
                {t('continueToApp') || 'Continuar a la app'}
              </Button>
              <Button 
                onClick={() => setShowInstallModal(false)}
                variant="outline"
                className="w-full"
              >
                {t('close') || 'Cerrar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Welcome Modal */}
      <Dialog open={showWelcome} onOpenChange={setShowWelcome}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <img src="/logo-transparent.png" alt="AllergenApp Logo" className="w-32 h-32" />
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm leading-relaxed whitespace-pre-line text-gray-700">
              {t('welcomeMessage')}
            </p>
            <div className="flex flex-col gap-4">
              <Button 
                onClick={handleInstallClick}
                className="w-full bg-[#8FBC8F] hover:bg-[#7CAA7C] py-6 text-lg"
                data-testid="download-app-btn"
              >
                <Download className="w-5 h-5 mr-2" />
                {t('installOnMobile')}
              </Button>
              
              <div className="flex items-center justify-between gap-4">
                <select
                  data-testid="welcome-language-selector"
                  value={currentLang}
                  onChange={(e) => changeLanguage(e.target.value)}
                  className="text-sm border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-[#8FBC8F]"
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                <Button 
                  onClick={handleCloseWelcome}
                  variant="outline"
                  data-testid="close-welcome-btn"
                >
                  {t('close')}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <div className="min-h-screen bg-gradient-to-br from-[#f5f9f5] via-white to-[#e8f5e9] relative">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left side - Text */}
          <div className="space-y-8">
            <div className="flex flex-wrap items-start justify-between mb-6 gap-4">
              <div className="flex items-center gap-4">
                <img src="/logo-transparent.png" alt="Logo" className="w-24 h-24" />
                <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
                  {t('appTitle')}
                </h1>
              </div>
              
              <div className="flex flex-col items-end gap-3">
                <Button
                  onClick={handleClose}
                  variant="ghost"
                  size="icon"
                  className="bg-white shadow-lg hover:bg-gray-100 rounded-full"
                  data-testid="close-landing-btn"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </Button>
              </div>
            </div>
            
            <div>
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                {t('heroTitle')}
              </h2>
              <p className="text-xl text-gray-600 font-medium">
                {t('heroSubtitle')}
              </p>
            </div>

            <div className="flex flex-col gap-4 max-w-md">
              <Button
                onClick={handleInstallClick}
                size="lg"
                className="bg-[#8FBC8F] hover:bg-[#7CAA7C] text-white px-8 py-6 text-lg h-auto rounded-xl shadow-lg hover:shadow-xl transition-all w-full"
                data-testid="install-mobile-btn"
              >
                <Download className="w-6 h-6 mr-3" />
                {t('installOnMobile')}
              </Button>
              
              <Button
                onClick={() => navigate('/pricing')}
                size="lg"
                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-8 py-6 text-lg h-auto rounded-xl shadow-lg hover:shadow-xl transition-all w-full"
                data-testid="premium-btn"
              >
                <Crown className="w-6 h-6 mr-3" />
                {t('getPremium') || 'Obtener Premium'}
              </Button>
              
              <Button
                onClick={() => navigate('/contribute')}
                size="lg"
                variant="outline"
                className="px-8 py-6 text-lg h-auto rounded-xl border-2 border-[#8FBC8F] text-[#8FBC8F] hover:bg-[#8FBC8F] hover:text-white w-full transition-all"
                data-testid="contribute-btn"
              >
                <Coffee className="w-5 h-5 mr-3" />
                {t('contribute')}
              </Button>
            </div>
          </div>

          {/* Right side - Phone mockup */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-full max-w-[300px] h-auto aspect-[1/2] bg-gray-900 rounded-[3rem] p-3 shadow-2xl mx-auto">
                <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden">
                  <img
                    src="/screenshots/scan-screen.jpg"
                    alt="App Screenshot"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class=\"flex items-center justify-center h-full bg-gradient-to-br from-green-50 to-green-100\"><div class=\"text-center p-8\"><img src=\"/logo-transparent.png\" class=\"w-20 h-20 mx-auto mb-4\" /><p class=\"text-gray-600\">AllergenApp</p></div></div>';
                    }}
                  />
                </div>
              </div>
              <div className="absolute -z-10 top-10 -right-10 w-64 h-64 bg-[#8FBC8F] rounded-full opacity-20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-900">
            {t('whatItDoes')}
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {features.map((feature, idx) => (
              <Card key={idx} className="border-2 border-[#8FBC8F]/20 hover:border-[#8FBC8F] transition-all hover:shadow-lg">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="flex justify-center">
                    <div className="p-4 bg-[#8FBC8F]/10 rounded-full">
                      {feature.icon}
                    </div>
                  </div>
                  <p className="text-gray-700 font-medium">
                    ✔ {feature.text}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-br from-[#f5f9f5] to-white">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-900">
            {t('benefits')}
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="border-2 border-[#8FBC8F]/20 hover:border-[#8FBC8F] transition-all hover:shadow-lg">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="text-4xl">🌱</div>
                <div>
                  <p className="text-gray-700 font-medium text-lg">
                    {t('benefitAllergies')}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#8FBC8F]/20 hover:border-[#8FBC8F] transition-all hover:shadow-lg">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="text-4xl">🍼</div>
                <div>
                  <p className="text-gray-700 font-medium text-lg">
                    {t('benefitParents')}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#8FBC8F]/20 hover:border-[#8FBC8F] transition-all hover:shadow-lg">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="text-4xl">🛒</div>
                <div>
                  <p className="text-gray-700 font-medium text-lg">
                    {t('benefitSupermarket')}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#8FBC8F]/20 hover:border-[#8FBC8F] transition-all hover:shadow-lg">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="text-4xl">🚫</div>
                <div>
                  <p className="text-gray-700 font-medium text-lg">
                    {t('benefitNoReading')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section className="py-16 bg-gradient-to-br from-[#f5f9f5] to-white">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-900">
            {t('realScreenshots')}
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Screenshot 1 - Scan screen */}
            <div className="space-y-4">
              <Card className="overflow-hidden shadow-lg">
                <CardContent className="p-0">
                  <img
                    src="/screenshots/scan-screen.jpg"
                    alt="Pantalla de escaneo"
                    className="w-full h-[500px] object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </CardContent>
              </Card>
              <p className="text-center text-gray-600 font-medium">{t('scanScreen')}</p>
            </div>

            {/* Screenshot 2 - Product result */}
            <div className="space-y-4">
              <Card className="overflow-hidden shadow-lg">
                <CardContent className="p-0">
                  <img
                    src="/screenshots/product-result.jpg"
                    alt="Resultado del producto"
                    className="w-full h-[500px] object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </CardContent>
              </Card>
              <p className="text-center text-gray-600 font-medium">{t('productResult')}</p>
            </div>

            {/* Screenshot 3 - Allergen selection */}
            <div className="space-y-4">
              <Card className="overflow-hidden shadow-lg">
                <CardContent className="p-0">
                  <img
                    src="/screenshots/allergen-selection.jpg"
                    alt="Selección de alérgenos"
                    className="w-full h-[500px] object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </CardContent>
              </Card>
              <p className="text-center text-gray-600 font-medium">{t('allergenSelection')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Section */}
      <section className="py-16 bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full">
                <Crown className="w-12 h-12 text-white" />
              </div>
            </div>
            
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              {t('premiumTitle') || 'Versión Premium'}
            </h3>
            
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              {t('premiumDescription') || 'Descarga la versión Premium para disfrutar de funciones avanzadas y ayudar a mantener la app.'}
            </p>
            
            <div className="grid md:grid-cols-5 gap-4 mb-10">
              <Card className="border-2 border-yellow-200 bg-white">
                <CardContent className="p-4 text-center">
                  <div className="text-green-500 font-bold text-xl mb-1">✔</div>
                  <p className="text-gray-700 font-medium">{t('premiumNoAds') || 'Sin publicidad'}</p>
                </CardContent>
              </Card>
              
              <Card className="border-2 border-yellow-200 bg-white">
                <CardContent className="p-4 text-center">
                  <div className="text-green-500 font-bold text-xl mb-1">✔</div>
                  <p className="text-gray-700 font-medium">{t('premiumMoreSuggestions') || 'Más sugerencias'}</p>
                </CardContent>
              </Card>
              
              <Card className="border-2 border-yellow-200 bg-white">
                <CardContent className="p-4 text-center">
                  <div className="text-green-500 font-bold text-xl mb-1">✔</div>
                  <p className="text-gray-700 font-medium">{t('premiumUnlimitedFavorites') || 'Favoritos ilimitados'}</p>
                </CardContent>
              </Card>
              
              <Card className="border-2 border-yellow-200 bg-white">
                <CardContent className="p-4 text-center">
                  <div className="text-green-500 font-bold text-xl mb-1">✔</div>
                  <p className="text-gray-700 font-medium">{t('premiumUnlimitedProfiles') || 'Perfiles ilimitados'}</p>
                </CardContent>
              </Card>
              
              <Card className="border-2 border-yellow-200 bg-white">
                <CardContent className="p-4 text-center">
                  <div className="text-green-500 font-bold text-xl mb-1">✔</div>
                  <p className="text-gray-700 font-medium">{t('premiumSupport') || 'Apoya el proyecto'}</p>
                </CardContent>
              </Card>
            </div>
            
            <Button
              onClick={() => navigate('/pricing')}
              size="lg"
              className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-12 py-6 text-xl h-auto rounded-xl shadow-lg hover:shadow-xl transition-all"
            >
              <Crown className="w-6 h-6 mr-3" />
              {t('getPremiumNow') || 'Obtener Premium - €1,99/mes'}
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#8FBC8F]">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-3xl font-bold text-white mb-6">
            {t('startNow')}
          </h3>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            {t('joinThousands')}
          </p>
          <Button
            onClick={handleInstallClick}
            size="lg"
            className="bg-white text-[#8FBC8F] hover:bg-gray-100 px-8 py-6 text-lg h-auto rounded-xl shadow-lg hover:shadow-xl transition-all"
          >
            <Download className="w-6 h-6 mr-3" />
            {t('installOnMobile')}
          </Button>
        </div>
      </section>
      </div>
    </>
  );
};
